import { TextStatesData, TextureData, TextureStatesData } from "../../../types/types";
import { ApplyMixins } from "../../../utils/ApplyMixins";
import { Interactive } from "../../../mixins/Interactive";
import { Showable } from "../../../mixins/Showable";
import { Toggleable } from "../../../mixins/Toggleable";
import { UpdateView } from "../../../mixins/UpdateView";
import { ISize } from "../../../controls/scrollpane/ISize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { RectSize } from "../../../utils/RectSize";
import { AppSettings } from "../../managers/AppSettings";
import { SettingsItem } from "../options/SettingsItem";


/**
 * Yellow label item on gray/black background as header 
 * with 3 titles/labels on top of (one of the) results page/content table(s)
 */
export class DealsHeaderItem 
    extends SettingsItem
    implements ISize
{
    public label2: Phaser.GameObjects.Text;
    public label3: Phaser.GameObjects.Text;
    public label4: Phaser.GameObjects.Text;
    public label5: Phaser.GameObjects.Text;

    /**
     * reference width of any element (doesnt consider dynamic element size)
     * used to calc item's position in one of the 5 columns
     * - the calculation does consider borderPadding, 
     *   as padding for far left-right elements
     * NOTE: same as @see DealsListItem::elementReferenceWidth
     */
    protected elementReferenceWidth: number = 70;

    protected bgTextureData: TextureData;
    protected labelTextStyle: Phaser.Types.GameObjects.Text.TextStyle;

    // delimiter line
    public imgDelimiterLine: Phaser.GameObjects.Image;
    protected delimiterLineAlpha: number;


    public Init(
        bgTextureData: TextureData = undefined, 
        labelTextStyle: Phaser.Types.GameObjects.Text.TextStyle = undefined,
        delimiterLineAlpha: number,
    ): void
    {
        this.bgTextureData = bgTextureData;
        this.labelTextStyle = labelTextStyle;
        this.delimiterLineAlpha = delimiterLineAlpha;

        this.size = new RectSize();
    }

    public Build(): void
    {
        this.bg = this.scene.make.image({x:0, y:0, key:this.bgTextureData.texture, frame:this.bgTextureData.frame});
        this.bg.setOrigin(0);
        this.add(this.bg);

        this.label = this.scene.make.text({style: this.labelTextStyle});
        this.label.setOrigin(0, 0.5);
        this.add(this.label);

        this.label2 = this.scene.make.text({style: this.labelTextStyle});
        this.label2.setOrigin(0.5);
        this.add(this.label2);

        this.label3 = this.scene.make.text({style: this.labelTextStyle});
        this.label3.setOrigin(0.5);
        this.add(this.label3);

        this.label4 = this.scene.make.text({style: this.labelTextStyle});
        this.label4.setOrigin(0.5);
        this.add(this.label4);

        this.label5 = this.scene.make.text({style: this.labelTextStyle});
        this.label5.setOrigin(1, 0.5);
        this.add(this.label5);
        
        this.imgDelimiterLine = this.scene.make.image({key: LoaderFileKeys.ui, frame:"rect-2x2-white.png"});
        this.imgDelimiterLine.setOrigin(0, 0.5);
        this.imgDelimiterLine.alpha = this.delimiterLineAlpha;
        this.add(this.imgDelimiterLine);
    }

    public SetData(
        label: string = undefined,
        label2: string = undefined,
        label3: string = undefined,
        label4: string = undefined,
        label5: string = undefined,
    ): void
    {
        this.label.text = label;
        this.label2.text = label2;
        this.label3.text = label3;
        this.label4.text = label4;
        this.label5.text = label5;
    }
    
    /**
     * @alike: OnResize
     * Uses 'maximum allowed width' to compute item's resulting height
     * - Item's width and height depends on height of label textfield
     * - label's height depends on amount of HORIZONTAL space and maximum horizontal space allowed for item
     * 
     * @param width 
     */
    public override SetWidthGetHeight(width: number): number
    {
        //
        const totalSpacePerElement = width/5;
        this.label.style.setWordWrapWidth(totalSpacePerElement);
        this.label2.style.setWordWrapWidth(totalSpacePerElement);
        this.label3.style.setWordWrapWidth(totalSpacePerElement);
        this.label4.style.setWordWrapWidth(totalSpacePerElement);
        this.label5.style.setWordWrapWidth(totalSpacePerElement);

        const highestLabelDisplayHeight = Math.max(this.label.displayHeight, this.label2.displayHeight, this.label3.displayHeight)

        // computes and returns height
        return highestLabelDisplayHeight + 2*this.borderPadding.vertical;
    }

    public override OnResize(): void
    {
        const centerX = this.size.width/2;
        const centerY = this.size.height/2;
        const item2PosX = this.borderPadding.horizontal + this.elementReferenceWidth/2 + (this.size.width/2 - this.borderPadding.horizontal - this.elementReferenceWidth/2)/2;
        const item4PosX = this.size.width -this.borderPadding.horizontal - this.elementReferenceWidth/2 - (this.size.width/2 - this.borderPadding.horizontal - this.elementReferenceWidth/2)/2;
        
        this.bg.setDisplaySize(this.size.width, this.size.height);
        
        // origin 0,1 (bot left)
        this.imgDelimiterLine.y = this.size.height;
        this.imgDelimiterLine.displayWidth = this.size.width;

        // label origin (0, 0.5)
        this.label.setPosition(this.borderPadding.horizontal, centerY);

        this.label2.setPosition(item2PosX, centerY);

        this.label3.setPosition(centerX, centerY);

        this.label4.setPosition(item4PosX, centerY);

        // this.label5.setPosition(this.size.width - this.borderPadding.horizontal - this.elementReferenceWidth/2, centerY);
        this.label5.setPosition(this.size.width - this.borderPadding.horizontal, centerY);
    }
}